import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, InputAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnChange, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableCloseEvent, ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames, InfusionTerminateAliases } from '../../../codegen';

import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import { TerminateInfusionForm, TerminateRequest } from '../../../interfaces/infusion';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { ADEP_E1113Parameters } from '../../../utils/parameters';
import RouteUtils, { RouteError } from '../../../utils/route-utils';
import { TimeUtils } from '../../../utils/time-utils';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { SimpleDialog } from '../../dialog-helper';
import { InfusionsViewerParams } from '../infusion-viewer/infusions-viewer-params';
import SolutionService from '../services/solution-service';

import './infusion-terminate-controller.css'

interface TerminateResponseBody {
  messageDescription: string;
}

interface AbortErrorInterface {
  body: {
    errors: string[]
  }
}

@Controller({fragment: FragmentNames.INFUSION_TERMINATE})
class InfusionTerminateController extends BaseController {

  @Inject(InfusionTerminateAliases.INFUSED_VOLUME_UNIT)
  infusedUnitTextApi!: TextAPI;

  @Inject(InfusionTerminateAliases.DISCARDED_VOLUME_UNIT)
  discardedUnitTextApi!: TextAPI;

  @Inject(InfusionTerminateAliases.VOLUME_STEP_TEXT)
  stepVolumeTextApi!: TextAPI;

  @Inject(InfusionTerminateAliases.PARTIAL_VOLUME_TEXT)
  partialVolumeTextApi!: TextAPI;

  @Inject(InfusionTerminateAliases.FORM_INFUSION_TERMINATE)
  formApi!: FormAPI;

  @Inject(InfusionTerminateAliases.INFUSED_VOLUME_FIELD)
  infusedVolumeFieldApi!: FieldAPI;

  @Inject(InfusionTerminateAliases.DISCARDED_VOLUME_FIELD)
  discardedVolumeFieldApi!: FieldAPI;

  @Inject(InfusionTerminateAliases.INFUSED_VOLUME_INPUT)
  infusedVolumeInputApi!: InputAPI;

  @Inject(InfusionTerminateAliases.DISCARDED_VOLUME_INPUT)
  discardedVolumeInputApi!: InputAPI;

  @Inject(InfusionTerminateAliases.DT_CURRENT_TIME_INPUT)
  currentTimeInputApi!: InputAPI;

  isOnVolumeInputChnge = false;

  solutionService = new SolutionService();

  @OnMount(InfusionTerminateAliases.LAYOUT_INFUSION_TERMINATE)
  onLayoutMount(): void {
    const {
      patientId,
      encounterId,
      prescriptionNumber,
      sequenceNumber,
    } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      patientId,
      prescriptionNumber,
      sequenceNumber,
    });

    this.initializeParams();
    this.getManagementSolution();

    InformationBarUtils.collapsedBar = true;
  }

  initializeParams(): void {
    const automaticallyCalculateInfusedVolumeOfSolutions = this.getBoolParam(ADEP_E1113Parameters.AUTOMATICALLY_CALCULATE_INFUSED_VOLUME_SOLUTIONS);
    this.context.set({automaticallyCalculateInfusedVolumeOfSolutions});
  }

  getBoolParam(param: string): boolean {
    return 'S' === FoundationUtils.findModuleParam('ADEP#1113', param);
  }

  private async getManagementSolution(): Promise<void> {
    const { prescriptionNumber } = this.historyService.getCurrentRouteParams();
    const solutionSequence = InfusionsViewerParams.adminSchedule.solutionSequenceNumber as number;
    const routeSafeGetManagementSolution = RouteUtils.routeSafeAsync(this.solutionService.getManagementSolution);
    const automaticallycalculaterInfusedParam = this.context.get('automaticallyCalculateInfusedVolumeOfSolutions') as boolean;
    let infusedVolume;
    try {
      const managementSolution = await routeSafeGetManagementSolution(Number(prescriptionNumber), solutionSequence);
      const partialVolume = managementSolution.partialVolumeQuantityBI || managementSolution.partialVolumeQuantity;
      if (automaticallycalculaterInfusedParam) {
        infusedVolume = managementSolution.infusedVolumeQuantity;
      } else {
        infusedVolume = managementSolution.emarVolumeQuantity;
      }

      const solutionDetail = {
        despisedVolumeQty: '',
        emarVolumeQuantity: managementSolution.emarVolumeQuantity,
        infusedVolumeQty: infusedVolume,
        partialVolumeQuantity: partialVolume,
        solutionDescription: managementSolution.solutionDescription,
        unitMeasureCode: managementSolution.unitMeasureCode,
        volumeInStepQty: managementSolution.emarVolumeQuantity,
      }
      const qtDespisedVolume = Number(this.calculateVolumeStr(solutionDetail, Number(infusedVolume)));
      solutionDetail.despisedVolumeQty = qtDespisedVolume >= 0 ? String(qtDespisedVolume) : '0';
      this.formApi.setData(solutionDetail);
      const measureLabel = MessageService.translate('tws.measure-label');
      this.infusedUnitTextApi.setValue(measureLabel);
      this.discardedUnitTextApi.setValue(measureLabel);
      this.stepVolumeTextApi.setValue(`${managementSolution.emarVolumeQuantity || '0'} ${measureLabel}`,);
      this.partialVolumeTextApi.setValue(`${solutionDetail.despisedVolumeQty} ${measureLabel}`);
      TimeUtils.setDateToInput(this.currentTimeInputApi);
    } catch(error) {
      const isRouteError = error instanceof RouteError
      if(!isRouteError) {
        SimpleDialog.showErrorMessage(error as string);
      }
    }
  }

  @OnChange(InfusionTerminateAliases.INFUSED_VOLUME_FIELD)
  onInfusedVolumeChange(evt: ChangeEvent<FieldAPI>): void {
    this.updateInputValueOnce(this.discardedVolumeFieldApi, Number(evt.value));
  }

  @OnChange(InfusionTerminateAliases.DISCARDED_VOLUME_FIELD)
  onDiscardedVolumeChange(evt: ChangeEvent<FieldAPI>): void {
    this.updateInputValueOnce(this.infusedVolumeFieldApi, Number(evt.value));
  }

  updateInputValueOnce(field: FieldAPI, value: number): void {
    if(!this.isOnVolumeInputChnge){
      this.isOnVolumeInputChnge = true;
      const currentRecord = this.formApi.getRecord();
      field.setValue(this.calculateVolumeStr(currentRecord, value));
    } else {
      this.isOnVolumeInputChnge = false;
    }
  }

  updateFormOnTimeout(): void {
    setTimeout(() => {
      this.showToast('label.infusion-viewer.invalid-infusion-volume-warning', ToastLevel.WARNING);
      this.formApi.setEditing(true);
      this.formApi.setLoading(false);
    }, 1000);
  }

  createRequestBody(formRecord: TerminateInfusionForm): Record<string, Value> {
    const {
      encounterId,
      prescriptionNumber,
      currentSpeedInfusion,
      sequenceNumber: solutionNumber,
      startDate
    } = this.historyService.getCurrentRouteParams() as Record<string, Value>;

    const body = {
      dateChange: moment().format(ISO_FULL_DATE_FORMAT),
      dateHour: moment(startDate as string).format(ISO_FULL_DATE_FORMAT),
      discardedVolumeQuantity: Number(formRecord.despisedVolumeQty),
      encounterNumber: Number(encounterId),
      infusedVolumeQuantity: Number(formRecord.infusedVolumeQty),
      observationDescription: formRecord.observationDescription || '',
      partialVolumeQuantity: Number(formRecord.partialVolumeQuantity),
      prescriptionNumber: Number(prescriptionNumber),
      solutionNumber: Number(solutionNumber),
      typeDosage: formRecord.isTypeDosage,
      volumeSafeQuantity: Number(formRecord.volumeInStepQty),
    } as TerminateRequest;

    if(String(currentSpeedInfusion).toLocaleLowerCase() !== 'null') {
      body.infusionSpeedQuantity = Number(currentSpeedInfusion);
    }

    return body as Record<string, Value>;
  }

  @OnSubmit(InfusionTerminateAliases.FORM_INFUSION_TERMINATE)
  onFormSubmit(): void {
    this.formApi.setLoading(true);

    const formRecord = this.formApi.getRecord() as unknown as TerminateInfusionForm;

    const emarVolumeQuantity = Number(formRecord.volumeInStepQty);
    const totalVolumeQuantity = Number(formRecord.infusedVolumeQty) + Number(formRecord.partialVolumeQuantity);

    if(totalVolumeQuantity > emarVolumeQuantity) {
      this.updateFormOnTimeout();
      return;
    }

    const requestBody = this.createRequestBody(formRecord);
    this.handleTerminateSolutionRequest(requestBody);
  }

  @OnBeforeCancel(InfusionTerminateAliases.FORM_INFUSION_TERMINATE)
  onFormCancel(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }

  async handleTerminateSolutionRequest(requestBody: Record<string, Value>): Promise<void> {
    const {
      patientId,
      encounterId,
    } = this.historyService.getCurrentRouteParams();

    try {
      const response = await HttpService.post(Endpoint.ADEP_TERMINATE_SOLUTION, {}, { body: requestBody });

      this.handleTerminateSolutionRequestSuccess(response.body as TerminateResponseBody);
    } catch(error) {
      this.handleTerminateSolutionRequestError(error as AbortErrorInterface);
    } finally {
      applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
        encounterId,
        patientId,
      });
      applicationContext.clearNavigationStack();
    }
  }

  handleTerminateSolutionRequestSuccess(responseBody: TerminateResponseBody): void {
    if(responseBody.messageDescription) {
      this.showToast(responseBody.messageDescription, ToastLevel.WARNING);
    } else {
      this.showToast('label.infusion-viewer.toast-infusion-terminated', ToastLevel.SUCCESS);
    }
  }

  handleTerminateSolutionRequestError(abortError: AbortErrorInterface): void {
    if(abortError.body && abortError.body.errors) {
      abortError.body.errors.forEach(errorMessage => this.showToast(errorMessage, ToastLevel.ERROR));
    }
  }

  calculateVolumeStr(data: Record<string, Value> | TerminateInfusionForm, valueStr: number): string {
    const result = Number(data.volumeInStepQty) - Number(data.partialVolumeQuantity) - valueStr;
    return String(result);
  }

  showToast(message: string, toastLevel: ToastLevel): void {
    const toastId = 'infusion-terminate-toast';
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      id: toastId,
      level: toastLevel,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
    });
  }
}

export default InfusionTerminateController;
